import {
  type ChannelConnectionDetail,
  ChannelConnectionDetailSchema,
  type ChannelConnectionPage,
  ChannelConnectionPageSchema,
  type CreateChannelConnection,
  type ListChannelConnectionsQuery,
  TikTokConnectStartSchema,
  type UpdateChannelConnection,
} from '@sbc/domain-models/gateway';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet, apiSend } from './client';

const CONNECTIONS_KEY = ['admin', 'channel-connections'];

function listKey(query: ListChannelConnectionsQuery) {
  return [...CONNECTIONS_KEY, 'list', query] as const;
}

function detailKey(id: string) {
  return [...CONNECTIONS_KEY, 'detail', id] as const;
}

export function useChannelConnections(query: ListChannelConnectionsQuery) {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.search) {
    params.set('search', query.search);
  }
  return useQuery<ChannelConnectionPage>({
    queryKey: listKey(query),
    queryFn: () =>
      apiGet(`/api/admin/channel-connections?${params.toString()}`, ChannelConnectionPageSchema),
  });
}

export function useChannelConnection(id: string) {
  return useQuery<ChannelConnectionDetail>({
    queryKey: detailKey(id),
    queryFn: () => apiGet(`/api/admin/channel-connections/${id}`, ChannelConnectionDetailSchema),
  });
}

export function useCreateChannelConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateChannelConnection) =>
      apiSend('POST', '/api/admin/channel-connections', input, ChannelConnectionDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY }),
  });
}

export function useConnectTikTok() {
  return useMutation({
    mutationFn: () =>
      apiSend('POST', '/api/gateway/tiktok/oauth/start', {}, TikTokConnectStartSchema),
    onSuccess: ({ authorizationUrl }) => {
      window.location.assign(authorizationUrl);
    },
  });
}

export function useUpdateChannelConnection(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateChannelConnection) =>
      apiSend('PUT', `/api/admin/channel-connections/${id}`, input, ChannelConnectionDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY }),
  });
}
